import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { voiceMeta } from '@src/components/aiVoices/voiceLabels';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { KeyboardEvent } from 'react';

// Enable-dubbing toggle + activated-AI-Voice pool picker for the workspace
// (DE3). Twin of BrandKit/ShowcaseLanguageSettings — reads the saved activation
// off `organization` and persists every change immediately via the shared
// `useVoiceActivation` hook (same path the audition panel's Add/Remove uses, so
// the two surfaces never diverge). No Save/Clear buttons and no draft state.
// No audio playback here; per-voice auditioning is PLAT-10500.
const ShowcaseAIVoicesPanel = observer(() => {
  const dubbingStore = useDubbingStore();
  const {
    organization,
    dubbingEnabled,
    activatedVoices,
    activatedIds,
    busy,
    setDubbingEnabled,
    addVoice,
    removeVoice,
  } = useVoiceActivation();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Belt-and-suspenders: the catalog is eagerly hydrated at workspace init, but
  // hydrate() is idempotent per showcase so this is a no-op if already loaded.
  useEffect(() => {
    if (organization?.id) dubbingStore.hydrate(organization.id);
  }, [dubbingStore, organization?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const pool = dubbingStore.catalog.filter(v => !activatedIds.has(v.id));
    if (!trimmed) return pool;
    return pool.filter(v => {
      const name = v.name.toLowerCase();
      const provider = (v.provider || '').toLowerCase();
      const internal = v.internalName.toLowerCase();
      return (
        name.includes(trimmed) ||
        provider.includes(trimmed) ||
        internal.includes(trimmed)
      );
    });
  }, [dubbingStore.catalog, activatedIds, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) return;
    const el = dropdownRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const handleAdd = (voice: AiVoiceRecord) => {
    void addVoice(voice);
    setQuery('');
    inputRef.current?.focus();
  };

  const handleRemove = (id: string) => {
    void removeVoice(id);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Backspace' &&
      query.length === 0 &&
      activatedVoices.length > 0
    ) {
      e.preventDefault();
      const last = activatedVoices[activatedVoices.length - 1];
      handleRemove(last.id);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      if (filteredOptions.length === 0) return;
      setActiveIndex(i => Math.min(i + 1, filteredOptions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      if (filteredOptions.length === 0) return;
      setActiveIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Home') {
      if (filteredOptions.length === 0) return;
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === 'End') {
      if (filteredOptions.length === 0) return;
      e.preventDefault();
      setActiveIndex(filteredOptions.length - 1);
      return;
    }
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      const idx = Math.min(
        Math.max(activeIndex, 0),
        filteredOptions.length - 1,
      );
      handleAdd(filteredOptions[idx]);
      return;
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const comboboxDisabled = busy || dubbingStore.loadingStatus === 'loading';

  return (
    <Container>
      <ToggleRow>
        <div className="text">
          <div className="title">Enable AI dubbing</div>
          <div className="hint">
            {dubbingEnabled
              ? 'Stories in this showcase can be dubbed using the AI Voices you activate below.'
              : 'Turn on to dub stories into translated languages using AI Voices.'}
          </div>
        </div>
        <Switch
          role="switch"
          aria-checked={dubbingEnabled}
          aria-label="Enable AI dubbing"
          data-on={dubbingEnabled}
          disabled={busy || !organization}
          onClick={() => setDubbingEnabled(!dubbingEnabled)}
          type="button"
        >
          <span className="thumb" />
        </Switch>
      </ToggleRow>

      {dubbingEnabled && (
        <>
          <Description>
            Choose the AI Voices this showcase can dub with. Only activated
            voices are available when mapping a speaker. Changes save
            automatically.
          </Description>

          <Combobox
            ref={comboboxRef}
            onClick={() => {
              if (!comboboxDisabled) {
                setIsOpen(true);
                inputRef.current?.focus();
              }
            }}
            isOpen={isOpen}
            isDisabled={comboboxDisabled}
          >
            <div className="field">
              {activatedVoices.map(voice => (
                <Chip key={voice.id}>
                  <span className="labels">
                    <span className="caption">{voice.name}</span>
                    <span className="code">
                      {voiceMeta(voice.provider, voice.gender)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="remove"
                    onClick={e => {
                      e.stopPropagation();
                      handleRemove(voice.id);
                    }}
                    aria-label={`Remove ${voice.name}`}
                    disabled={busy}
                  >
                    ×
                  </button>
                </Chip>
              ))}
              <input
                ref={inputRef}
                value={query}
                placeholder={
                  activatedVoices.length === 0
                    ? 'Type to search AI Voices (e.g. Rachel, elevenlabs)…'
                    : 'Add another…'
                }
                onChange={e => {
                  setQuery(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleInputKeyDown}
                disabled={comboboxDisabled}
              />
            </div>
            {isOpen && (
              <Dropdown ref={dropdownRef} role="listbox">
                {dubbingStore.loadingStatus === 'loading' ? (
                  <EmptyState>Loading AI Voices…</EmptyState>
                ) : filteredOptions.length === 0 ? (
                  <EmptyState>
                    {dubbingStore.catalog.length === 0
                      ? 'No AI Voices are configured.'
                      : query.trim()
                        ? `No voices match "${query}".`
                        : 'All available voices are already selected.'}
                  </EmptyState>
                ) : (
                  filteredOptions.map((voice, index) => (
                    <button
                      key={voice.id}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      data-option-index={index}
                      data-active={index === activeIndex}
                      className="option"
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => {
                        e.stopPropagation();
                        handleAdd(voice);
                      }}
                    >
                      <span className="caption">{voice.name}</span>
                      <span className="code">
                        {voiceMeta(voice.provider, voice.gender)}
                      </span>
                    </button>
                  ))
                )}
              </Dropdown>
            )}
          </Combobox>
        </>
      )}
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    .title {
      color: var(--foreground);
      font-size: var(--text-base);
      font-weight: var(--font-weight-semibold);
    }
    .hint {
      color: var(--text-muted);
      font-size: var(--text-sm);
      line-height: 1.5;
    }
  }
`;

const Switch = styled.button<{ 'data-on': boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: ${({ 'data-on': on }) =>
    on ? 'var(--primary)' : 'var(--surface-elevated)'};
  cursor: pointer;
  padding: 0;
  transition: var(--transition-normal);

  .thumb {
    position: absolute;
    top: 50%;
    left: ${({ 'data-on': on }) => (on ? 'calc(100% - 22px)' : '2px')};
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ 'data-on': on }) =>
      on ? 'var(--background)' : 'var(--text-muted)'};
    transition: var(--transition-normal);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Description = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin: 0;
`;

const Combobox = styled.div<{ isOpen: boolean; isDisabled: boolean }>`
  position: relative;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'text')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};

  .field {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    min-height: 48px;
    padding: 6px 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: transparent;
    transition: var(--transition-normal);

    &:focus-within {
      border-color: var(--primary);
    }

    > input {
      flex: 1;
      min-width: 160px;
      border: none;
      outline: none;
      background: transparent;
      padding: 6px 4px;
      color: var(--foreground);
      font-size: var(--text-sm);
      font-family: var(--font-primary);

      &::placeholder {
        color: var(--text-muted);
      }
      &:disabled {
        cursor: not-allowed;
      }
    }
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 6px 2px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--foreground);
  font-size: var(--text-sm);

  /* Two-level layout: name on top, "Gender · Source" beneath. */
  .labels {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .caption {
    font-weight: var(--font-weight-medium);
    line-height: 1.25;
  }

  .code {
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: 1.2;
  }

  .remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-muted);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    border-radius: 999px;
    transition: var(--transition-normal);

    &:hover:not(:disabled) {
      background-color: var(--surface-elevated);
      color: var(--destructive);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100005;
  max-height: 320px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-md);
  padding: 4px 0;

  .option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 10px 16px;
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    color: var(--foreground);
    transition: var(--transition-normal);

    &[data-active='true'] {
      background: var(--primary);
      color: var(--background);

      .code {
        color: var(--background);
        opacity: 0.85;
      }
    }

    .caption {
      font-size: var(--text-sm);
    }

    .code {
      color: var(--text-muted);
      font-size: var(--text-xs);
    }
  }
`;

const EmptyState = styled.div`
  padding: 12px 16px;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

export default ShowcaseAIVoicesPanel;
